import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";

import { withAuthenticationRequired } from "@auth0/auth0-react";
import Loading from "../../components/loading";
import InboundManifestComponent from "../../components/manifest/inbound-manifest";
import { useContext, useEffect, useState, useMemo } from "react";
import { UserAuthenticatedContext } from "../../components/profile";
import { useParams } from "react-router-dom";
import { createWorkerFactory } from "@shopify/react-web-worker";
import { Client } from "../../external";

import "@glideapps/glide-data-grid/dist/index.css";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";

export interface InboundManifestRow {
  id?: string;
}

const createClientsWorker = createWorkerFactory(
  () => import("../../workers/clients")
);

const ManifestPage = (): JSX.Element => {
  const [userToken, setUserToken] = useState<string>();
  const [clients, setClients] = useState<Client[]>();
  const [arrivalDate, setArrivalDate] = useState<Date>(new Date());
  const [selectedClient, setSelectedClient] = useState<string | undefined>();
  const [selectedLoadReference, setSelectedLoadReference] = useState<
    string | undefined
  >();

  // Create worker only once using useMemo
  const clientsWorker = useMemo(() => createClientsWorker(), []);
  const { manifestId } = useParams();

  const userAuthenticatedContext = useContext(UserAuthenticatedContext);

  useEffect(() => {
    let isMounted = true;
    if (userAuthenticatedContext.token) {
      (async () => {
        const fetchedClients = await clientsWorker.getClients(
          userAuthenticatedContext.token!
        );

        // Only update state if component is still mounted
        if (isMounted) {
          setClients(fetchedClients);
        }
      })();
    }

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [userAuthenticatedContext.token, clientsWorker]); // Include only the token, not the entire context

  useEffect(() => {
    if (userAuthenticatedContext.token) {
      setUserToken(userAuthenticatedContext.token);
    }
  }, [userAuthenticatedContext.token]);

  if (!userToken || !manifestId || !clients) {
    return (
      <Container>
        <Loading />
      </Container>
    );
  }

  return (
    <div className="w-100 container-fluid">
      <Row className="align-items-end">
        <Col className="d-flex flex-column">
          <Form.Group className="mb-0" controlId="uploadFile">
            <Form.Label>&nbsp;</Form.Label>
            <Button className="form-control">Upload File</Button>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="formClientCode">
            <Form.Label>Client Code</Form.Label>
            <Form.Select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
            >
              {clients.map((client) => (
                <option key={client.clientId} value={client.clientId}>
                  {client.clientName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="formArrivalDate">
            <Form.Label>Arrival Date</Form.Label>
            <br />
            <DatePicker
              className="form-control w-100"
              selected={arrivalDate}
              onChange={(date) =>
                setArrivalDate(date === null ? new Date() : date)
              }
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="formLoadReference">
            <Form.Label>Load Reference</Form.Label>
            {/* TODO: load Load References from DB */}
            <InputGroup>
              <Form.Select
                value={selectedLoadReference}
                onChange={(e) => setSelectedLoadReference(e.target.value)}
              >
                {clients.map((client) => (
                  <option key={client.clientId} value={client.clientId}>
                    {client.clientName}
                  </option>
                ))}
              </Form.Select>
              <Button variant="outline-info" id="button-addon1">
                Refresh
              </Button>
            </InputGroup>
          </Form.Group>
        </Col>
        <Col className="d-flex flex-column">
          <Form.Group controlId="submitManifest">
            <Form.Label>&nbsp;</Form.Label>
            <Button variant="success" className="form-control">
              Submit Manifest
            </Button>
          </Form.Group>
        </Col>
      </Row>
      <InboundManifestComponent
        userToken={userToken}
        manifestId={manifestId}
        clients={clients}
      />
    </div>
  );
};

export default withAuthenticationRequired(ManifestPage, {
  onRedirecting: () => <Loading />,
});
